import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMainStore } from "@/stores";
import BorderBottomCard from "../../BorderBottomCard";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import { CHANNEL_ICONS } from "@/shared/utils/dashboardConfig";
import {
  getAccountProfilePath,
  getDominantTopEngagedChannel,
  safeFormatTimeAgo,
  sanitizeCompanyLogoUrl,
  summarizeTopEngagedScoreBreakdown,
  summarizeChannelMetricsLine,
  topEngagedChannelTitle,
} from "@/features/dashboard/utils/accountsDashboardUtils";
import { getCleanName } from "@/shared/utils/accountConfig";
import {
  getEngagementStatusVariant,
  getTextInCapitalize,
} from "@/shared/utils/helperFunctions";
import { useCreateAccountFromContacts } from "@/features/accounts/hooks/useAccounts";

const TopEngagedCard = ({ account }) => {
  const navigate = useNavigate();
  const user = useMainStore((state) => state.user);
  const [createRequested, setCreateRequested] = useState(false);
  const { mutate: createAccount, isPending: isCreatingAccount } =
    useCreateAccountFromContacts();
  const canCreateAccount = Boolean(
    !createRequested &&
      !account?.account_id &&
      account?.can_create_account &&
      account?.contact_ids?.length,
  );
  const path = account?.account_id ? getAccountProfilePath(account) : null;
  const engagementScore =
    Number(account?.engagement_score ?? account?.signal_count) || 0;
  const temperature = account?.engagement_temperature || "cold";
  const name = account?.company_name ?? "—";
  const lastSignalSuffix = account?.last_signal_at
    ? safeFormatTimeAgo(account.last_signal_at)
    : "—";

  const dominantChannel = useMemo(
    () => getDominantTopEngagedChannel(account?.signal_counts),
    [account?.signal_counts],
  );

  const outreachLine = useMemo(() => {
    if (!dominantChannel) return null;
    return summarizeChannelMetricsLine(
      account?.channel_metrics,
      dominantChannel,
    );
  }, [account?.channel_metrics, dominantChannel]);

  const scoreBreakdownLine = useMemo(
    () => summarizeTopEngagedScoreBreakdown(account?.score_breakdown),
    [account?.score_breakdown],
  );

  const DominantChannelIcon =
    dominantChannel != null ? CHANNEL_ICONS[dominantChannel] : null;

  const go = () => {
    if (path) navigate(path);
  };

  const handleCreateAccount = (event) => {
    event.stopPropagation();
    if (!canCreateAccount || !user?.id) return;
    createAccount({
      userId: user.id,
      contactIds: account.contact_ids,
    }, {
      onSuccess: () => setCreateRequested(true),
    });
  };

  return (
    <BorderBottomCard
      className={`${path ? "cursor-pointer" : ""} hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors`}
      role={path ? "button" : undefined}
      tabIndex={path ? 0 : undefined}
      onClick={go}
      onKeyDown={(e) => {
        if (path && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          go();
        }
      }}
    >
      <div className="flex gap-x-2 items-start w-full">
        <CommonAvatar
          imageUrl={sanitizeCompanyLogoUrl(account?.company_logo)}
          name={getCleanName(name)}
          className="rounded-full object-cover min-w-8 min-h-8 w-8 h-8 shrink-0"
          size="md"
        />

        <div className="flex flex-col gap-1 w-full min-w-0">
          <div className="flex w-full items-center justify-between gap-x-2 min-w-0">
            <h5 className="text-gray-800 dark:text-gray-100 text-[0.75rem] leading-5 font-medium truncate">
              {getCleanName(name)}
            </h5>
            <div className="flex shrink-0 items-center gap-1">
              <StatusChip
                label={getTextInCapitalize(temperature)}
                variant={getEngagementStatusVariant(temperature)}
              />
              <StatusChip label={`${engagementScore} pts`} variant="neutral" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 text-gray-500 dark:text-gray-400 text-[0.69rem] leading-tight font-normal">
            <span className="tabular-nums whitespace-nowrap shrink-0">
              Last signal: {lastSignalSuffix}
            </span>
            {Number(account?.engagement_sources?.direct_email_replies) > 0 && (
              <span>Direct email · {account.engagement_sources.direct_email_replies} replies</span>
            )}
            {Number(account?.engagement_sources?.campaign_email_replies) > 0 && (
              <span>Campaign email · {account.engagement_sources.campaign_email_replies} replies</span>
            )}
          </div>

          {scoreBreakdownLine ? (
            <p
              className="text-[0.65rem] leading-snug text-gray-500 dark:text-gray-400 line-clamp-2"
              title={scoreBreakdownLine}
            >
              Score: {scoreBreakdownLine}
            </p>
          ) : null}

          {canCreateAccount ? (
            <button
              type="button"
              onClick={handleCreateAccount}
              disabled={isCreatingAccount}
              className="w-fit text-[0.68rem] font-medium text-secondary hover:text-secondary/80 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isCreatingAccount ? "Creating account..." : "Create account"}
            </button>
          ) : null}

          {outreachLine && dominantChannel ? (
            <p className="text-[0.65rem] leading-snug text-gray-500 dark:text-gray-400 line-clamp-2">
              <span className="inline-flex items-center gap-1 align-middle font-semibold text-gray-600 dark:text-gray-300">
                {DominantChannelIcon ? (
                  <DominantChannelIcon
                    size={13}
                    strokeWidth={2}
                    className="shrink-0 text-secondary dark:text-secondary-light"
                    aria-hidden
                  />
                ) : null}
                <span>{topEngagedChannelTitle(dominantChannel)}</span>
              </span>
              <span className="text-gray-400 dark:text-gray-500"> · </span>
              {outreachLine}
            </p>
          ) : null}
        </div>
      </div>
    </BorderBottomCard>
  );
};

export default TopEngagedCard;
